// Small, dependency-free SVG charts for the stats page.
//
// One series per chart, so one colour (the brand red) does all the work and no
// legend is needed; the card title says what is plotted. Each chart has a
// crosshair tooltip and a table twin, so nothing is readable only by hover.

import React, { useMemo, useRef, useState } from "react";

export const MARK = '#6d0303';                 // $theme_red
const WASH = 'rgba(109, 3, 3, 0.10)';          // area fill: the hue as a wash
const GRID = '#e9e3d9';                        // one step off the card surface
const INK_MUTED = '#777';
const W = 640;
const PAD = { t: 16, r: 44, b: 28, l: 8 };   // right pad holds the y-axis labels

export const fmtNum = (n) => Number(n ?? 0).toLocaleString('ar-EG');
export const fmtCompact = (n) => {
    n = Number(n ?? 0);
    if (n >= 1_000_000) return (n / 1_000_000).toLocaleString('ar-EG', { maximumFractionDigits: 1 }) + ' م';
    if (n >= 1_000) return (n / 1_000).toLocaleString('ar-EG', { maximumFractionDigits: 1 }) + ' ألف';
    return n.toLocaleString('ar-EG');
};
const fmtDay = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('ar', { day: 'numeric', month: 'short' });
const fmtDayLong = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('ar', { weekday: 'short', day: 'numeric', month: 'long' });

function niceTicks(max) {
    if (max <= 0) return [0, 1];
    const raw = max / 3;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 5, 10].map(m => m * mag).find(s => s >= raw) || mag * 10;
    const out = [];
    for (let v = 0; v <= max + step * 0.999; v += step) out.push(v);
    return out;
}

/** Card chrome shared by every chart: title, optional subtitle, table toggle. */
export function ChartCard({ title, subtitle, children, table }) {
    const [showTable, setShowTable] = useState(false);
    return (
        <section className='st-card'>
            <header className='st-card-head'>
                <div>
                    <h3>{title}</h3>
                    {subtitle && <p>{subtitle}</p>}
                </div>
                {table && (
                    <button type='button' className={'st-toggle' + (showTable ? ' is-on' : '')}
                            onClick={() => setShowTable(v => !v)} aria-pressed={showTable}>
                        {showTable ? 'رسم' : 'جدول'}
                    </button>
                )}
            </header>
            {showTable && table ? table : children}
        </section>
    );
}

/**
 * Time series: a 2px line with a 10% area wash, hairline grid, the last value
 * labelled, and a crosshair that snaps to the nearest day.
 * data: [{date: 'YYYY-MM-DD', value}]
 */
export function TimeSeries({ data, height = 200, unit = '' }) {
    const ref = useRef(null);
    const [hover, setHover] = useState(null);   // index
    const H = height;

    const { pts, ticks, max } = useMemo(() => {
        const max = Math.max(1, ...data.map(d => d.value));
        const ticks = niceTicks(max);
        const top = ticks[ticks.length - 1];
        const n = data.length;
        const x = (i) => PAD.l + (n === 1 ? 0 : (i / (n - 1)) * (W - PAD.l - PAD.r));
        const y = (v) => PAD.t + (1 - v / top) * (H - PAD.t - PAD.b);
        return { pts: data.map((d, i) => ({ x: x(i), y: y(d.value), ...d })), ticks: ticks.map(t => ({ t, y: y(t) })), max: top };
    }, [data, H]);

    if (!data.length) return <p className='st-empty'>لا بيانات</p>;

    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    const area = `${line} L${pts[pts.length - 1].x.toFixed(1)},${(H - PAD.b).toFixed(1)} L${pts[0].x.toFixed(1)},${(H - PAD.b).toFixed(1)} Z`;
    const last = pts[pts.length - 1];

    const onMove = (e) => {
        const box = ref.current.getBoundingClientRect();
        const px = ((e.clientX - box.left) / box.width) * W;
        let best = 0, bestD = Infinity;
        pts.forEach((p, i) => { const d = Math.abs(p.x - px); if (d < bestD) { bestD = d; best = i; } });
        setHover(best);
    };
    const h = hover != null ? pts[hover] : null;
    // x-axis: first, last, and up to three evenly spaced labels between
    const labelIdx = [...new Set([0, Math.round((pts.length - 1) / 4), Math.round((pts.length - 1) / 2), Math.round(3 * (pts.length - 1) / 4), pts.length - 1])];

    return (
        <div className='st-plot' onPointerLeave={() => setHover(null)}>
            <svg ref={ref} viewBox={`0 0 ${W} ${H}`} className='st-svg' onPointerMove={onMove}
                 role='img' aria-label='رسم بياني زمني'>
                {ticks.map(({ t, y }) => (
                    <g key={t}>
                        <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke={GRID} strokeWidth='1' />
                        <text x={W - PAD.r + 6} y={y + 4} fontSize='11' fill={INK_MUTED} style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtCompact(t)}</text>
                    </g>
                ))}
                <path d={area} fill={WASH} />
                <path d={line} fill='none' stroke={MARK} strokeWidth='2' strokeLinejoin='round' strokeLinecap='round' />
                {/* end label, the one direct label on the chart */}
                <circle cx={last.x} cy={last.y} r='4' fill={MARK} stroke='#fff' strokeWidth='2' />
                {labelIdx.map(i => (
                    <text key={i} x={pts[i].x} y={H - 8} fontSize='11' fill={INK_MUTED}
                          textAnchor={i === 0 ? 'start' : i === pts.length - 1 ? 'end' : 'middle'}>{fmtDay(pts[i].date)}</text>
                ))}
                {h && (
                    <g>
                        <line x1={h.x} x2={h.x} y1={PAD.t} y2={H - PAD.b} stroke={INK_MUTED} strokeWidth='1' />
                        <circle cx={h.x} cy={h.y} r='5' fill={MARK} stroke='#fff' strokeWidth='2' />
                    </g>
                )}
            </svg>
            {h && (
                <div className='st-tip' style={{ left: `${(h.x / W) * 100}%` }}>
                    <strong>{fmtNum(h.value)}{unit && ` ${unit}`}</strong>
                    <span>{fmtDayLong(h.date)}</span>
                </div>
            )}
            <span className='sr-only'>آخر قيمة {fmtNum(last.value)}؛ الحد الأعلى للمحور {fmtNum(max)}</span>
        </div>
    );
}

export function SeriesTable({ data, label }) {
    const rows = [...data].reverse().filter(d => d.value !== 0);
    if (!rows.length) return <p className='st-empty'>لا شيء في هذه الفترة</p>;
    return (
        <div className='st-table-wrap'>
            <table className='st-table'>
                <thead><tr><th>اليوم</th><th>{label}</th></tr></thead>
                <tbody>{rows.map(d => <tr key={d.date}><td>{fmtDayLong(d.date)}</td><td>{fmtNum(d.value)}</td></tr>)}</tbody>
            </table>
        </div>
    );
}

/**
 * Horizontal bars for a short categorical list (countries): one colour, bars
 * <= 20px thick with rounded data-ends, value at the tip, hover lifts the row.
 * data: [{label, value}]
 */
export function BarList({ data }) {
    const [hover, setHover] = useState(null);
    if (!data.length) return <p className='st-empty'>لا بيانات</p>;
    const max = Math.max(1, ...data.map(d => d.value));
    return (
        <ul className='st-bars' onPointerLeave={() => setHover(null)}>
            {data.map((d, i) => (
                <li key={d.label} className={hover === i ? 'is-hover' : ''} onPointerEnter={() => setHover(i)}
                    title={`${d.label}: ${fmtNum(d.value)}`}>
                    <span className='st-bar-label'>{d.label}</span>
                    <span className='st-bar-track'>
                        <span className='st-bar-fill' style={{ width: `${(d.value / max) * 100}%` }} />
                    </span>
                    <span className='st-bar-value'>{fmtNum(d.value)}</span>
                </li>
            ))}
        </ul>
    );
}

/** Ordered columns (ratings 1–5): one colour, since the order is the x-axis. */
export function Columns({ data }) {
    const [hover, setHover] = useState(null);
    const max = Math.max(1, ...data.map(d => d.value));
    return (
        <div className='st-columns' onPointerLeave={() => setHover(null)}>
            {data.map((d, i) => (
                <div key={d.label} className={'st-col' + (hover === i ? ' is-hover' : '')} onPointerEnter={() => setHover(i)}>
                    <span className='st-col-value'>{d.value ? fmtNum(d.value) : ''}</span>
                    <span className='st-col-track'>
                        <span className='st-col-fill' style={{ height: `${(d.value / max) * 100}%` }} />
                    </span>
                    <span className='st-col-label'>{d.label}</span>
                </div>
            ))}
        </div>
    );
}

/** Stat tile: label, value, optional delta vs the previous period, 12-point sparkline. */
export function StatTile({ label, value, delta, spark }) {
    const pts = useMemo(() => {
        if (!spark?.length) return null;
        const s = spark.slice(-12);
        const max = Math.max(1, ...s);
        return s.map((v, i) => `${(i / Math.max(1, s.length - 1)) * 60},${22 - (v / max) * 20}`).join(' ');
    }, [spark]);
    return (
        <div className='st-tile'>
            <span className='st-tile-label'>{label}</span>
            <span className='st-tile-value'>{fmtCompact(value)}</span>
            <span className='st-tile-foot'>
                {delta != null && (
                    <span className={'st-tile-delta ' + (delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : '')}>
                        {delta > 0 ? '+' : ''}{fmtNum(delta)} في الفترة
                    </span>
                )}
                {pts && (
                    <svg viewBox='0 0 60 24' className='st-spark' aria-hidden='true'>
                        <polyline points={pts} fill='none' stroke={MARK} strokeWidth='1.5' strokeLinejoin='round' />
                    </svg>
                )}
            </span>
        </div>
    );
}
